import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Fade,
  Stack,
  IconButton,
  Typography,
  styled,
  useTheme,
} from '@mui/material';

import {
  setUpdatesBannerClicked,
  setUpdatesBannerClosed,
  useListenUserChanges,
} from '../../utils/db';
import { useFirebaseLoginState } from '../../utils/hooks';
import { IconClose } from '../minicomponents/icons';
import { IconRocketLaunchFilled } from '../minicomponents/icons';

interface StructuredOutputBannerProps {
  onTryNow: () => void;
}

const BannerContainer = styled(Box)(() => ({
  background: 'white',
  padding: '8px 16px',
  borderRadius: 'theme.shape.borderRadius',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'auto',
  border: '1px dashed rgba(0,0,0,0.2)',
}));

const BANNER_NAME = 'structured_output_smm';

export function StructuredOutputBanner({ onTryNow }: StructuredOutputBannerProps) {
  const { t } = useTranslation();
  const { userId } = useFirebaseLoginState();
  const { dbUser, loading } = useListenUserChanges(userId);
  const [isVisible, setIsVisible] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    if (loading || !dbUser) {
      setIsVisible(false);
      return;
    }

    const bannersFlags = dbUser?.flags?.banners;
    if (bannersFlags && bannersFlags[`${BANNER_NAME}_closed`]) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
  }, [dbUser, loading]);

  const handleClose = () => {
    if (userId) {
      setUpdatesBannerClosed(userId, BANNER_NAME);
    }
    setIsVisible(false);
  };

  const handleTryNowClick = () => {
    if (userId) {
      setUpdatesBannerClicked(userId, BANNER_NAME);
    }
    onTryNow();
    handleClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Fade in={isVisible} timeout={500}>
      <BannerContainer>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconRocketLaunchFilled
            sx={{
              mr: 1,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          />
          <Typography variant="body1" sx={{ pr: 2 }}>
            {t('app.banners.structuredOutput.text')}
          </Typography>
          <Button
            variant="contained"
            onClick={handleTryNowClick}
            sx={{
              height: '32px',
              background: 'linear-gradient(90deg, #3D4BF5, #A57DD3)',
              borderRadius: '4px',
              '&:hover': {
                opacity: 0.9,
              },
              whiteSpace: 'nowrap',
            }}
          >
            {t('app.banners.structuredOutput.cta')}
          </Button>
          <IconButton onClick={handleClose} size="small" sx={{ height: '32px' }}>
            <IconClose />
          </IconButton>
        </Stack>
      </BannerContainer>
    </Fade>
  );
}
